import React, { createContext, useContext, useState, useCallback } from 'react';
import './SidebarMenu.scss';
import { Icon } from '@design-system';
import { GeneralMenuItems, MenuItemsType, ParentItemType } from '../../utils/menu';
import { SidebarMenuItem } from './SidebarMenuItem/SidebarMenuItem';
import { DoubleArrowLeft, DoubleArrowRight } from '@icons';
import { useDispatch, useSelector } from 'react-redux';
import { getSidebarState, toggleSidebar } from '@features/menu';

type SidebarMenuProps = { items: MenuItemsType[]; activeParentItem: ParentItemType };

// Contexte pour gérer l'état global des menus
interface MenuContextType {
    openMenus: Set<string>;
    openMenu: (menuId: string) => void;
    closeMenu: (menuId: string) => void;
    closeAllMenus: () => void;
    closeAllMenusExcept: (menuIdToKeep: string) => void;
    toggleMenu: (menuId: string) => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

// Hook pour utiliser le contexte des menus
/* eslint-disable react-refresh/only-export-components */
export const useMenuContext = () => {
    const context = useContext(MenuContext);
    if (!context) {
        throw new Error('useMenuContext must be used within a MenuProvider');
    }
    return context;
};

// Provider pour le contexte des menus
const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());

    const openMenu = useCallback((menuId: string) => {
        setOpenMenus((prev) => new Set(Array.from(prev).concat(menuId)));
    }, []);

    const closeMenu = useCallback((menuId: string) => {
        setOpenMenus((prev) => {
            const newSet = new Set(prev);
            newSet.delete(menuId);
            return newSet;
        });
    }, []);

    const closeAllMenus = useCallback(() => {
        setOpenMenus(new Set());
    }, []);

    const closeAllMenusExcept = useCallback((menuIdToKeep: string) => {
        setOpenMenus(new Set([menuIdToKeep]));
    }, []);

    const toggleMenu = useCallback((menuId: string) => {
        setOpenMenus((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(menuId)) {
                newSet.delete(menuId);
            } else {
                newSet.add(menuId);
            }
            return newSet;
        });
    }, []);

    const value: MenuContextType = {
        openMenus,
        openMenu,
        closeMenu,
        closeAllMenus,
        closeAllMenusExcept,
        toggleMenu
    };

    return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

export const SidebarMenu: React.FC<SidebarMenuProps> = ({ items, activeParentItem }) => {
    const dispatch = useDispatch();
    const isOpen = useSelector(getSidebarState);

    return (
        <MenuProvider>
            <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
                <div className="toggler" onClick={() => dispatch(toggleSidebar())}>
                    <Icon icon={isOpen ? <DoubleArrowLeft /> : <DoubleArrowRight />} size={16} bordered rounded color="primary" />
                </div>

                <div className="top-sidebar">
                    {GeneralMenuItems.map((item, index) => (
                        <SidebarMenuItem
                            key={index}
                            item={item}
                            isActive={activeParentItem === item.parentItem}
                            color={item.color}
                            intrinsic="h3"
                        />
                    ))}
                </div>

                <div className="bottom-sidebar">
                    {items.map((item, index) => (
                        <SidebarMenuItem key={index} item={item} color={item.color} intrinsic="span" />
                    ))}
                </div>
            </div>
        </MenuProvider>
    );
};
